import DashboardCharts from "@/components/dashboard/DashboardCharts";
import DashboardStats from "@/components/dashboard/DashboardStats";
import { dashboardService, DashboardStatsResponse } from "@/services/dashboard.service";
import { authService } from "@/services/auth.service/auth.api";
import { TicketIcon, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

function formatRelativeTime(dateStr: string | undefined) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffM = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);
  if (diffM < 60) return `Há ${diffM} minuto${diffM !== 1 ? "s" : ""}`;
  if (diffH < 24) return `Há ${diffH} hora${diffH !== 1 ? "s" : ""}`;
  return `Há ${diffD} dia${diffD !== 1 ? "s" : ""}`;
}

const statusBadge: Record<string, { label: string; color: string }> = {
  open: { label: "Aberto", color: "bg-blue-500/10 text-blue-600 border-blue-200" },
  in_progress: { label: "Em andamento", color: "bg-orange-500/10 text-orange-600 border-orange-200" },
  resolved: { label: "Resolvido", color: "bg-emerald-500/10 text-emerald-600 border-emerald-200" },
};

const priorityBadge: Record<string, { label: string; color: string }> = {
  low: { label: "Baixa", color: "bg-slate-100 text-slate-500" },
  medium: { label: "Média", color: "bg-yellow-500/10 text-yellow-600" },
  high: { label: "Alta", color: "bg-orange-500/10 text-orange-600" },
  urgent: { label: "Urgente", color: "bg-red-500/10 text-red-600" },
};

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const user = authService.getCurrentUser() as any;
  const userName = user?.name || user?.user?.name || "Usuário";

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await dashboardService.getStats();
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Bom dia";
    if (hour < 18) return "Boa tarde";
    return "Boa noite";
  };

  return (
    <>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{greeting()},</p>
          <h1 className="text-3xl font-extrabold tracking-tight mt-0.5">{userName}</h1>
          <p className="text-muted-foreground mt-1">Aqui está sua visão geral de hoje.</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 shrink-0"
          onClick={loadStats}
          disabled={isLoading}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      <DashboardStats
        openTickets={stats?.openTickets}
        totalTickets={stats?.totalTickets}
        totalMessages={stats?.totalMessages}
        totalClients={stats?.totalClients}
        slaFirstResponse={stats?.slaFirstResponse}
        avgFirstResponseMinutes={stats?.avgFirstResponseMinutes}
        isLoading={isLoading}
      />

      <div className="flex flex-col gap-6 mt-6">
        <DashboardCharts
          volumeByDay={stats?.volumeByDay}
          topTicketTags={stats?.topTicketTags}
          topClientsByTickets={stats?.topClientsByTickets}
          topAgentsByTickets={stats?.topAgentsByTickets}
          ticketsByStatus={stats?.ticketsByStatus}
          ticketsByChannel={stats?.ticketsByChannel}
          isLoading={isLoading}
        />

        <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm opacity-0 animate-fadeIn animate-delay-300">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Atividades Recentes</h3>
            <Link to="/tickets">
              <Button variant="ghost" size="sm" className="text-xs h-7 gap-1">
                Ver todos
              </Button>
            </Link>
          </div>
          <div className="space-y-1">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-start gap-4 p-3 rounded-xl">
                    <div className="h-10 w-10 rounded-xl bg-muted animate-pulse shrink-0" />
                    <div className="flex-1 space-y-2 pt-1">
                      <div className="h-4 bg-muted rounded-lg w-1/3 animate-pulse" />
                      <div className="h-3 bg-muted rounded-lg w-2/3 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : stats?.recentTickets?.length ? (
              stats.recentTickets.map((ticket) => {
                const status = statusBadge[ticket.status] || { label: ticket.status, color: "bg-muted text-muted-foreground" };
                const priority = priorityBadge[ticket.priority as string];
                return (
                  <Link
                    key={ticket._id}
                    to={`/tickets/${encodeURIComponent(ticket.protocolId)}`}
                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-muted/50 transition-colors group"
                  >
                    <div className="bg-primary/10 text-primary p-2.5 rounded-xl shrink-0 group-hover:bg-primary/20 transition-colors">
                      <TicketIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">
                        {ticket.title || `Ticket ${ticket.protocolId || ticket._id}`}
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <code className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                          {ticket.protocolId || ticket._id}
                        </code>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${status.color}`}>
                          {status.label}
                        </span>
                        {priority && (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${priority.color}`}>
                            {priority.label}
                          </span>
                        )}
                        {ticket.clientName && (
                          <span className="text-xs text-muted-foreground">{ticket.clientName}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0 hidden sm:block">
                      {formatRelativeTime(ticket.createdAt)}
                    </span>
                  </Link>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <TicketIcon className="h-10 w-10 text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground text-sm">Nenhuma atividade recente.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
